import { useState } from 'react';
import { Box, BoxProps, Stack, Typography } from '@mui/material';
import { OnboardingLayout } from 'components/OnboardingLayout';
import { useOnboardingProgress } from 'hooks/useOnboardingProgress';
import { Trans, useTranslation } from 'react-i18next';
import CriticalAlertsIcon from 'assets/critical-flood-alert.svg?react';
import { useHistory } from 'react-router-dom';
import { useOnboardingState } from 'state/useOnboardingState';
import { requestCriticalAlertsPermission } from 'shared/utils';

const iconContainerSx: BoxProps['sx'] = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  '& svg': {
    width: '90vmin',
    height: 'auto',
    maxWidth: 345,
  },
};

export const CriticalAlertsPermissionContent = (): JSX.Element => {
  const { t } = useTranslation();
  const history = useHistory();
  const { setCriticalAlertsRequested } = useOnboardingState();
  const [isPrompting, setIsPrompting] = useState(false);
  const progress = useOnboardingProgress('critical_alerts');

  const handleContinue = async (): Promise<void> => {
    setCriticalAlertsRequested(true);
    setIsPrompting(true);
    await requestCriticalAlertsPermission();
    setIsPrompting(false);
    history.push('/onboarding_notifications');
  };

  const footerText = isPrompting ? (
    <Typography textAlign="center" sx={{ px: 2 }}>
      <Trans i18nKey="criticalAlertPermission.prompt">
        Tap <b>Allow</b> to receive critical alerts about extreme situations
        near you.
      </Trans>
    </Typography>
  ) : undefined;

  return (
    <OnboardingLayout
      buttonTitle={
        isPrompting ? undefined : t('criticalAlertPermission.button')
      }
      onClick={handleContinue}
      footerText={footerText}
      progress={progress}
      hideProgress={isPrompting}
    >
      {!isPrompting && (
        <Stack spacing={2}>
          <Box sx={iconContainerSx}>
            <CriticalAlertsIcon />
          </Box>

          <Typography
            variant="h2"
            align="center"
            data-testid="onboarding-title"
          >
            {t('criticalAlertPermission.title')}
          </Typography>

          <Typography>
            <Trans i18nKey="criticalAlertPermission.description.bypass">
              Watch Duty can alert you in critical situations even if your phone
              is <b>silenced</b> or in <b>Do Not Disturb</b>.
            </Trans>
          </Typography>

          <Typography>
            <Trans i18nKey="criticalAlertPermission.description.emergencyOnly">
              Critical Alerts are only used in <b>extreme situations</b> with
              <b>immediate threats</b> to life and safety.
            </Trans>
          </Typography>
        </Stack>
      )}
    </OnboardingLayout>
  );
};
